/* eslint-disable react/prop-types */
import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { format } from 'date-fns';

import TableActionsCell from './TableActionsCell';
import TableCellTooltip from './TableCellTooltip';

import { Tabs } from './ListView';
import AlarmModuleService, {
  IAlarmModuleService,
} from 'services/AlarmModuleService';
import {
  tableProps,
  topToolbarProps,
  notAvailableData,
  tableBodyRowProps,
  tableInitialState,
  tableHeadCellProps,
  actionsColumnBodyCellProps,
  actionsColumnHeadCellProps,
  searchFilterTextFieldProps,
  formatAlarmDefinitionForGrid,
  expandColumnTableHeadCellProps,
} from 'components/AlarmManagement/helpers';
import { AlarmDefinition } from 'types/types';
import { AlarmManagementPopupRef } from '../Popup/Popup';

interface Props {
  tabValue: Tabs;
  modalRef: React.RefObject<AlarmManagementPopupRef>;
  setModalType: React.Dispatch<React.SetStateAction<string>>;
  alarmIdToApplyAction: React.MutableRefObject<number | undefined>;
}

export interface Ref {
  take: number;
  currentPage: number;
  alarmsData?: AlarmDefinition[];
  fetchAlarmsData: (skip: number, activeStatus: string) => Promise<void>;
}
const take = -1;

const ListViewTable: ForwardRefRenderFunction<Ref, Props> = (
  { tabValue, setModalType, modalRef, alarmIdToApplyAction },
  ref
) => {
  const [page, setPage] = useState<number>(0);
  const [alarmsData, setAlarmsData] = useState<AlarmDefinition[]>([]);
  const memoizedAlarms = useMemo(() => alarmsData, [alarmsData]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [groupedColumns, setGroupedColumns] = useState<number>(1);
  //Calculate the dynamic width of the columns based on the window width
  const [dynamicWidth, setDynamicWidth] = useState(
    Math.round(window.innerWidth * 0.2)
  );

  const fetchAlarmsData = async (skip: number, activeStatus: string) => {
    const niService: IAlarmModuleService = new AlarmModuleService();
    try {
      setIsLoading(true);
      const data = await niService.GetAlarmDefinition(skip, take, activeStatus);
      setAlarmsData(
        formatAlarmDefinitionForGrid(
          data,
          notAvailableData
        ) as AlarmDefinition[]
      );
    } catch (error) {
      console.error('Failed to fetch alarm definitions', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderDateCell = ({ cell, row }) => {
    const date = cell.getValue();
    return date && date != notAvailableData
      ? format(new Date(date), 'dd/MM/yyyy HH:mm:ss')
      : date == notAvailableData
      ? ''
      : '';
  };

  const customDateFilter = (row, columnId, filterValue) => {
    const rowValue = row.getValue(columnId);
    if (!rowValue || !filterValue) return false;

    const date =
      rowValue && rowValue != notAvailableData
        ? format(new Date(rowValue), 'dd/MM/yyyy HH:mm:ss')
        : rowValue == notAvailableData
        ? ''
        : '';

    return date.includes(filterValue);
  };

  const columns = useMemo<MRT_ColumnDef<AlarmDefinition>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        size: dynamicWidth * 0.5,
        Cell: TableCellTooltip,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        size: dynamicWidth * 0.5,
        Cell: TableCellTooltip,
      },
      {
        accessorKey: 'execution_group',
        header: 'Group',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        size: dynamicWidth * 0.6,
        Cell: TableCellTooltip,
      },
      {
        accessorKey: 'time_aggregation_level',
        header: 'Frequency',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        size: dynamicWidth * 0.4,
        Cell: TableCellTooltip,
      },
      {
        accessorKey: 'severity',
        header: 'Severity',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        size: dynamicWidth * 0.35,
        Cell: TableCellTooltip,
      },
      {
        accessorKey: 'total_active_alarm_instances',
        header: 'Total Active Instances',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        size: dynamicWidth * 0.45,
        Cell: TableCellTooltip,
      },
      {
        accessorKey: 'latest_evaluated_period',
        header: 'Last Evaluated Period',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        filterFn: customDateFilter,
        Cell: renderDateCell,
        size: dynamicWidth * 0.55,
      },
      {
        accessorKey: 'latest_execution',
        header: 'Latest Execution',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        filterFn: customDateFilter,
        Cell: renderDateCell,
        size: dynamicWidth * 0.55,
      },
      {
        accessorKey: 'breachingPeriods',
        header: 'Breaching Periods',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        size: dynamicWidth * 0.85,
        Cell: TableCellTooltip,
      },
      {
        accessorKey: 'createdUser',
        header: 'Created User',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        Cell: TableCellTooltip,
        size: dynamicWidth * 0.55,
      },
      {
        accessorKey: 'createdDatetime',
        header: 'Created At',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        filterFn: customDateFilter,
        Cell: renderDateCell,
        size: dynamicWidth * 0.55,
      },
      {
        accessorKey: 'lastUpdatedUser',
        header: 'Last Updated User',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        Cell: TableCellTooltip,
        size: dynamicWidth * 0.55,
      },
      {
        accessorKey: 'lastUpdatedDatetime',
        header: 'Last Updated At',
        muiFilterTextFieldProps: searchFilterTextFieldProps,
        muiTableHeadCellProps: tableHeadCellProps,
        filterFn: customDateFilter,
        Cell: renderDateCell,
        size: dynamicWidth * 0.55,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableColumnActions: false,
        enableColumnOrdering: false,
        enableColumnDragging: false,
        muiTableHeadCellProps: actionsColumnHeadCellProps,
        muiTableBodyCellProps: actionsColumnBodyCellProps,
        Cell: ({ row }) => (
          <TableActionsCell
            modalRef={modalRef}
            setModalType={setModalType}
            alarmDefinition={row.original}
            alarmIdToApplyAction={alarmIdToApplyAction}
          />
        ),
      },
    ],
    [modalRef, setModalType, alarmIdToApplyAction, dynamicWidth]
  );

  const table = useMaterialReactTable({
    columns: columns,
    enableHiding: false,
    data: memoizedAlarms,
    enableGrouping: true,
    columnResizeMode: 'onEnd',
    muiTableProps: tableProps,
    enableDensityToggle: false,
    enableColumnResizing: true,
    groupedColumnMode: 'remove',
    enableFullScreenToggle: false,
    state: {
      isLoading: isLoading,
      showLoadingOverlay: false,
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        enableResizing: false,
        maxSize: dynamicWidth * groupedColumns,
        size:
          groupedColumns > 2
            ? dynamicWidth * groupedColumns * 0.3
            : dynamicWidth * 0.8,
        muiTableHeadCellProps: expandColumnTableHeadCellProps,
      },
    },
    initialState: tableInitialState,
    muiTopToolbarProps: topToolbarProps,
    muiTableBodyRowProps: tableBodyRowProps,
    localization: {
      move: 'Drag to group by',
    },
  });

  useImperativeHandle(ref, () => ({
    take,
    alarmsData,
    fetchAlarmsData,
    currentPage: page,
  }));

  useEffect(() => {
    setPage(0);
    fetchAlarmsData(0, tabValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  useEffect(() => {
    const handleResize = () => {
      setDynamicWidth(Math.round(window.innerWidth * 0.2));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const groupedColsLength = table.getState().grouping.length;
    if (groupedColsLength > 1 && groupedColumns !== groupedColsLength) {
      setGroupedColumns(groupedColsLength);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getState().grouping]);

  return (
    <div className="table-container">
      <MaterialReactTable table={table} />
    </div>
  );
};

export default forwardRef<Ref, Props>(ListViewTable);
